import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, TextField, FormControl, Chip, Autocomplete, ListItemText, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

// Edits a Service Provider event (see ScheduleEventModal). Unlike a session it has no customer and
// no task, and it must keep at least one employee.
function EditEventModal(props) {
  const [employeesToAdd, setEmployeesToAdd] = useState([]);
  const [employeesToRemove, setEmployeesToRemove] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [scheduleName, setScheduleName] = useState('');
  const [resource, setResource] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    scheduleName: false,
    employees: false,
    startTime: false,
    endTime: false
  });
  const isSubmittingRef = useRef(false);

  const assignedEmployees = () => {
    const removalIds = new Set(employeesToRemove.map(employee => employee.id));
    return props.employees.filter(employee => (props.schedule?.employee_ids || []).includes(employee.id) && !removalIds.has(employee.id));
  };

  const unassignedEmployees = () => {
    const assignedIds = new Set(props.schedule?.employee_ids || []);
    const options = props.employees.filter(employee => !assignedIds.has(employee.id));
    return props.user.permissions.ScheduleSessionForOtherEmployees ? options : options.filter(employee => employee.id === props.user.emp_id);
  };

  const handleDeleteEmployee = (employee) => {
    setEmployeesToRemove(prev => prev.some(item => item.id === employee.id) ? prev : [...prev, employee]);
  };

  const scheduleDateString = props.schedule?.date ? props.schedule.date.substring(0, 10) : '';

  const isScheduleChanged = () => (
    (startDate ? moment(startDate).format('YYYY-MM-DD') : '') !== scheduleDateString ||
    moment(startTime).format('HH:mm') !== moment(props.schedule?.start_time).format('HH:mm') ||
    moment(endTime).format('HH:mm') !== moment(props.schedule?.end_time).format('HH:mm') ||
    scheduleName !== (props.schedule?.schedule_name || '') ||
    (resource?.resource_id ?? null) !== (props.schedule?.resource_id ?? null) ||
    notes !== (props.schedule?.notes || '')
  );

  const validate = () => {
    const newErrors = {
      scheduleName: !scheduleName,
      employees: assignedEmployees().length + employeesToAdd.length === 0,
      startTime: !startTime || moment(startTime).isSameOrAfter(moment(endTime)),
      endTime: !endTime || moment(endTime).isSameOrBefore(moment(startTime))
    };
    setErrors(newErrors);
    setSubmitted(true);
    return !Object.values(newErrors).some(err => err);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmittingRef.current || !props.schedule) {
      return;
    }

    if (!validate()) {
      if (assignedEmployees().length + employeesToAdd.length === 0) {
        props.onError('An event needs at least one employee');
      }
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    const scheduleId = props.schedule.schedule_id;

    try {
      // Add before removing, so the event is never left without an employee.
      if (employeesToAdd.length > 0) {
        await axios.post(APIURL + '/master_schedule/employee', { schedule_id: scheduleId, employee_ids: employeesToAdd.map(employee => employee.id) });
      }
      for (const employee of employeesToRemove) {
        await axios.delete(APIURL + '/master_schedule/employee/' + employee.id, { data: { schedule_id: scheduleId } });
      }

      if (isScheduleChanged()) {
        const date = moment(startDate).format('YYYY-MM-DD');
        await axios.put(APIURL + '/master_schedule/' + scheduleId, {
          date,
          start_time: moment(`${date} ${moment(startTime).format('HH:mm')}`).utc().format('YYYY-MM-DD HH:mm:00'),
          end_time: moment(`${date} ${moment(endTime).format('HH:mm')}`).utc().format('YYYY-MM-DD HH:mm:00'),
          notes,
          resource_id: resource?.resource_id ?? null,
          max_capacity: props.schedule.max_capacity,
          schedule_name: scheduleName,
          task_type: props.schedule.task_type,
        });
      }

      props.onSuccess('Event updated successfully');
      props.reloadScheduleData();
      props.onClose();
    } catch (error) {
      props.onError(error.response?.data?.message || 'An error occurred while updating the event');
      props.reloadScheduleData();
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setSubmitted(false);
    setErrors({ scheduleName: false, employees: false, startTime: false, endTime: false });
    setEmployeesToAdd([]);
    setEmployeesToRemove([]);
    if (!props.open || !props.schedule) {
      return;
    }
    setScheduleName(props.schedule.schedule_name || '');
    setResource(props.resources.find(item => item.resource_id === props.schedule.resource_id) || null);
    setNotes(props.schedule.notes || '');
    setStartDate(scheduleDateString ? moment(scheduleDateString).toDate() : null);
    setStartTime(props.schedule.start_time ? moment(props.schedule.start_time).toDate() : null);
    setEndTime(props.schedule.end_time ? moment(props.schedule.end_time).toDate() : null);
  }, [props.open, props.schedule]);

  return (
    <Modal enforceFocus={false}
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="edit-event-modal-title"
    >
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 500, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '90vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography id="edit-event-modal-title" variant="h6" component="div" sx={{ mb: 1 }}>
            Edit Event
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={props.onClose}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Event Name"
            fullWidth
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            error={errors.scheduleName && submitted}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            sx={{ mb: 1, mt: 0.5 }}
          />
          <FormControl fullWidth sx={{ mb: 1 }}>
            <Autocomplete
              id="event-location-select"
              options={props.resources}
              value={resource}
              getOptionLabel={(option) => option.resource_name || ''}
              isOptionEqualToValue={(option, value) => option.resource_id === value.resource_id}
              onChange={(event, newValue) => setResource(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.resource_id}>
                  {option.resource_name}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Location (optional)" />}
            />
          </FormControl>
          <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
            {props.schedule?.task_type} {moment(props.schedule?.date).utc().format('MM/DD/YYYY')} {moment(props.schedule?.start_time).format('hh:mm A')} - {moment(props.schedule?.end_time).format('hh:mm A')}
          </Typography>
          <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
            Assigned Employees:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {assignedEmployees().map(employee => (
              <Chip key={employee.id} label={employee.employee_name} color="primary" onDelete={() => handleDeleteEmployee(employee)} />
            ))}
          </Box>
          {employeesToRemove.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {employeesToRemove.map(employee => (
                <Chip
                  key={`remove-${employee.id}`}
                  label={employee.employee_name}
                  color="error"
                  variant="outlined"
                  onDelete={() => setEmployeesToRemove(prev => prev.filter(item => item.id !== employee.id))}
                />
              ))}
            </Box>
          )}
          <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 2 }}>
            <Autocomplete
              multiple
              id="event-employee-select"
              options={unassignedEmployees()}
              getOptionLabel={(option) => option.employee_name}
              filterSelectedOptions
              value={employeesToAdd}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => setEmployeesToAdd(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Add Employees"
                  placeholder="Employees"
                  error={errors.employees && submitted}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <ListItemText primary={option.employee_name} />
                </li>
              )}
            />
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={startDate}
                sx={{ width: '36%' }}
                onChange={(newValue) => setStartDate(newValue)}
              />
              <TimePicker
                label="Start Time"
                value={startTime}
                slotProps={{ textField: { error: errors.startTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setStartTime(newValue)}
              />
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
                slotProps={{ textField: { error: errors.endTime && submitted } }}
                sx={{ width: '32%' }}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            margin="normal"
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 1 }}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 1 }}>
            {isSubmitting ? 'Saving…' : 'Edit Event'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default EditEventModal;
